import { useMemo } from 'react';

import type { IPaymentMethod } from '@endeavour/payment-integration';

import { config } from '../../../config/config';
import { useActivtyFeed } from '../../account/hooks/use-activity-feed';
import { useValueBeforeLoad } from '../../account/hooks/use-value-before-load';
import { EventCode } from '../../account/util/events.constants';
import { formatDate, formatFromDateToTimestampEpoc } from '../../shared/utils/date-format';
import { exists } from '../../shared/utils/filters';

function isTransactionWithTransactionAmount(
    transaction: unknown,
): transaction is { metadata: { details: { transaction_amount: number } } } {
    if (typeof transaction !== 'object' || transaction === null || !('metadata' in transaction)) {
        return false;
    }

    if (
        typeof transaction.metadata !== 'object' ||
        transaction.metadata === null ||
        !('details' in transaction.metadata)
    ) {
        return false;
    }

    if (
        typeof transaction.metadata.details !== 'object' ||
        transaction.metadata.details === null ||
        !('transaction_amount' in transaction.metadata.details)
    ) {
        return false;
    }

    return typeof transaction.metadata.details.transaction_amount === 'number';
}

export function usePresetList(paymentMethod?: IPaymentMethod) {
    const filters = useMemo(
        () => ({
            filters: {
                feedType: {
                    in: [EventCode.USER_TRANSACTION],
                },
                lastUpdatedOn: {
                    greaterThanOrEqualTo: formatFromDateToTimestampEpoc(
                        formatDate(new Date(new Date().setDate(new Date().getDate() - 90)), '-'),
                    ),
                },
            },
        }),
        [],
    );

    const { feed, fetching } = useActivtyFeed(filters);
    const viewFeed = useValueBeforeLoad(feed, fetching);

    const previousAmountsList = useMemo(() => {
        const previousAmounts: number[] =
            viewFeed
                ?.map((transaction) => {
                    if (!isTransactionWithTransactionAmount(transaction)) {
                        return;
                    }

                    return transaction.metadata.details.transaction_amount;
                })
                .filter(exists) ?? [];

        const sortedList = previousAmounts.sort(function (a, b) {
            return a - b;
        });

        let sortedUniqueList = [...new Set(sortedList)];

        if (sortedUniqueList.length === 0) {
            sortedUniqueList = config.CASHIER.default.DEFAULT_PRESET_AMOUNTS;
        }

        if (sortedUniqueList.length === 1) {
            if (paymentMethod?.min) {
                const minValue = sortedUniqueList[0] - 25;

                minValue > paymentMethod?.min
                    ? sortedUniqueList.push(paymentMethod?.min)
                    : sortedUniqueList.push(minValue);
            }
            if (paymentMethod?.max) {
                const maxValue = sortedUniqueList[0] + 25;

                maxValue > paymentMethod?.max
                    ? sortedUniqueList.push(maxValue)
                    : sortedUniqueList.push(paymentMethod?.max);
            }
        }

        if (sortedUniqueList.length === 2) {
            const average = Math.round((sortedUniqueList[0] + sortedUniqueList[1]) / 2);
            sortedUniqueList.push(average);
        }

        if (sortedUniqueList.length > 3) {
            sortedUniqueList = sortedUniqueList.splice(
                sortedUniqueList.length - 3,
                sortedUniqueList.length - 1,
            );
        }

        return sortedUniqueList.sort(function (a, b) {
            return a - b;
        });
    }, [viewFeed, paymentMethod]);

    return useMemo(
        () => ({
            previousAmountsList,
        }),
        [previousAmountsList],
    );
}
